import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { Segmented } from '../ds';
import {
  baselineNetCents, everythingCents, netIfSpentCents, outliersCents,
  spendingFromBudgetsCents, withTransfersCents,
} from '../../utils/dashboardN';
import FigureNote from './FigureNote';

// The rows that twin a Bottom line figure (#2207): pointing at either lights
// both.
const LINKED = new Set(['netIfSpent', 'baseline', 'everything']);

/**
 * Money in and out (#2139, epic #2138): the month as a short ledger. The
 * current month has two tabs, Budgeted (Income, Spending outside budgets,
 * Budgeted funds remaining, then Net, all budgets spent) and Raw
 * (Income, Spending from budgets, Spending outside budgets, then Baseline
 * net; Transfers, then Net with transfers counted; Outliers, then Net, no
 * exclusion). A past month shows the Raw rows under
 * one "Month Closed" tab, its last row reading "Net at close". The
 * three Net rows share one indent and carry no "=" prefix (#2165): the
 * indent, the hairline and the weight already mark a total. The first letter of Income, both Spending rows, Transfers and
 * Outliers takes the entry-type color (epic decision 14), heavier, no chip;
 * a screen reader hears the whole word.
 *
 * Amounts are signed as `/flows` keeps them and shown with accounting parens.
 * Colors (#2207, DESIGN_SYSTEM.md section 11): sums green above zero and
 * red below; Baseline net and Income white above zero and red below; every
 * other row white at any sign; zero dim everywhere.
 *
 * Row shape (#2207): at every width a row is name left, amount right,
 * spanning its container (the Month view's 460 px left column from lg up,
 * the whole card below). The note is hover only (2 seconds, at once on
 * focus, a tap on a phone). Row text is two points above the table value
 * size at every width. A row that twins a Bottom line figure lights with it
 * (`highlight`, `onHighlight`).
 *
 * @param {object} props
 * @param {object} props.month               the `/flows` month entry
 * @param {boolean} props.isCurrent
 * @param {number} props.positiveBudgetCents `/tangible` positiveBudgetCents
 * @param {('budgeted'|'raw')} props.tab     the current month's tab
 * @param {(tab: string) => void} props.onTabChange
 * @param {string|null} [props.highlight]    the linked figure being pointed at
 * @param {(key: string|null) => void} [props.onHighlight]
 * @param {Object} props.strings             engine strings
 */
const MoneyInOut = ({ month, isCurrent, positiveBudgetCents, tab, onTabChange, highlight = null, onHighlight, strings }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const s = (key) => strings[`dashboardN.${key}`];
  const rowFontPx = ds.font.value + 2;

  // Entry-type letter: the first letter in the type's color, the rest plain;
  // the outer aria-label keeps the word whole for screen readers.
  const typed = (label, type) => (
    <Box component="span" aria-label={label} sx={{ whiteSpace: 'nowrap' }}>
      <Box
        component="span"
        aria-hidden
        data-entry-type={type}
        sx={{ color: palette.entryType[type].fg, fontWeight: ds.font.weightMedium }}
      >
        {label.charAt(0)}
      </Box>
      <Box component="span" aria-hidden>{label.slice(1)}</Box>
    </Box>
  );

  const colorOf = (r) => {
    const negative = r.cents < 0;
    const zero = r.cents === 0;
    if (r.key === 'baseline' || r.key === 'income') return resolveAmountColor(palette, 'row', negative, zero);
    return resolveAmountColor(palette, r.total ? 'total' : 'neutral', negative, zero);
  };

  const raw = [
    { key: 'income', cents: (month.incomeCents || 0), type: 'income' },
    { key: 'spendingFromBudgets', cents: spendingFromBudgetsCents(month), type: 'spending' },
    { key: 'spendingOutside', cents: (month.unbudgetedSpendingCents || 0), type: 'spending' },
    { key: 'baseline', cents: baselineNetCents(month), total: true, noteKey: 'baseline.row' },
    { key: 'transfers', cents: (month.transferNetCents || 0), type: 'transfer' },
    { key: 'withTransfers', cents: withTransfersCents(month), total: true },
    { key: 'outliers', cents: outliersCents(month), type: 'onetime' },
    { key: isCurrent ? 'withOutliers' : 'everything', cents: everythingCents(month), total: true, noteKey: isCurrent ? 'withOutliers.note' : 'everything.note' },
  ];
  const budgeted = [
    { key: 'income', cents: (month.incomeCents || 0), type: 'income' },
    { key: 'spendingOutside', cents: (month.unbudgetedSpendingCents || 0), type: 'spending' },
    { key: 'budgetedRemaining', cents: -(positiveBudgetCents || 0) },
    { key: 'netIfSpent', cents: netIfSpentCents(month, positiveBudgetCents), total: true },
  ];
  const rows = isCurrent && tab === 'budgeted' ? budgeted : raw;

  const tabs = isCurrent ? (
    <Segmented
      inCard
      ariaLabel={s('tabs')}
      value={tab}
      onChange={onTabChange}
      options={[
        { value: 'budgeted', label: s('tabBudgeted') },
        { value: 'raw', label: s('tabRaw') },
      ]}
    />
  ) : (
    <Box
      component="span"
      data-testid="money-in-out-closed"
      sx={{
        fontSize: '12px',
        padding: '5px 14px',
        borderRadius: '5px',
        border: `1px solid ${palette.divider}`,
        color: palette.text.secondary,
        whiteSpace: 'nowrap',
      }}
    >
      {s('tabClosed')}
    </Box>
  );

  return (
    <Box data-testid="money-in-out">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: `${ds.space[4]}px`, mb: `${ds.space[3]}px` }}>
        <Typography component="h3" sx={{ m: 0, fontSize: `${ds.font.meta}px`, fontWeight: ds.font.weightMedium, color: palette.text.secondary }}>
          {s('moneyInOut')}
        </Typography>
        {tabs}
      </Box>
      <Box data-testid="money-in-out-rows">
        {rows.map((r) => {
          const label = s(r.key);
          const note = strings[`dashboardN.${r.noteKey || `${r.key}.note`}`];
          const name = r.type ? typed(label, r.type) : label;
          const linked = LINKED.has(r.key);
          // Pointer events, not mouse ones: FigureNote owns the mouse
          // handlers that open its note.
          const pointer = linked && onHighlight
            ? { onPointerEnter: () => onHighlight(r.key), onPointerLeave: () => onHighlight(null) }
            : {};
          return (
            <FigureNote
              key={r.key}
              note={note}
              enterDelay={2000}
              data-testid={`money-in-out-${r.key}`}
              data-highlight={linked && highlight === r.key ? 'true' : undefined}
              {...pointer}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                columnGap: `${ds.space[4]}px`,
                alignItems: 'baseline',
                width: 'calc(100% + 12px)',
                mx: '-6px',
                px: '6px',
                py: '6px',
                borderRadius: '4px',
                fontSize: `${rowFontPx}px`,
                borderTop: r.total ? `1px solid ${palette.divider}` : 'none',
                backgroundColor: linked && highlight === r.key ? alpha(palette.interactive, 0.10) : 'transparent',
                transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
              }}
            >
              <Box sx={{ minWidth: 0, pl: r.total ? `${ds.space[6]}px` : 0, color: r.total ? palette.text.primary : palette.text.secondary }}>{name}</Box>
              <MaskedCurrency
                value={r.cents / 100}
                style={{
                  color: colorOf(r),
                  fontWeight: r.total ? ds.font.weightMedium : ds.font.weightRegular,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              />
            </FigureNote>
          );
        })}
      </Box>
    </Box>
  );
};

export default MoneyInOut;
